import { FALLBACK_APP_NAME } from '@constants/locale';
import { MAP_TILES } from '@constants/map';
import { api, LOGIN_PATH } from './client';
import type { Permission, Role, Scope } from './common';

/**
 * The signed-in user (GET /auth/me): who, the role (for its label), where the access reaches (`scope`; an operator
 * works at its location) and what they may do (`permissions`, the only thing the UI checks).
 */
export interface Me {
  id: number;
  email: string;
  name: string;
  role: Role;
  scope: Scope;
  permissions: Permission[];
  /** where the dashboard opens for this role, decided by the server (Phase 4; absent on an older backend: home) */
  landing?: 'home' | 'statistics' | 'deliveries' | 'visits' | 'operations';
  /** two-step verification: on for this account, and whether the policy requires it (national admins); absent on an older backend */
  totp?: { enabled: boolean; required: boolean };
}

/**
 * The second step a correct password may lead to (POST /auth/login for an account with two-step verification, or one
 * that must enroll first): no session yet, only a short-lived pending one bound to the account (an httpOnly cookie).
 */
export type LoginStep = 'totp' | 'enroll';
/** POST /auth/login: the signed-in user (no two-step verification), or the step still to take. */
export type LoginAnswer = Me | { step: LoginStep };
/** Sent with the password: this client can ask for the second step (an older one is refused for such accounts). */
const TWO_STEP_HEADER = 'X-Two-Step';
export const isLoginStep = (a: LoginAnswer): a is { step: LoginStep } => 'step' in a && (a.step === 'totp' || a.step === 'enroll');

/** GET /meta: branding and map settings, also before sign-in. */
export interface Meta {
  appName: string;
  orgName: string;
  orgAddress: string | null;
  /** Name of the one OWNER_WAREHOUSE location (where cylinders are registered), null until it exists. */
  warehouseName: string | null;
  mapTileUrl: string;
  mapAttribution: string;
}

/** Shown until /meta answers (and if it never does). */
export const FALLBACK_META: Meta = {
  appName: FALLBACK_APP_NAME,
  orgName: '',
  orgAddress: null,
  warehouseName: null,
  mapTileUrl: MAP_TILES.url,
  mapAttribution: MAP_TILES.attribution,
};

export const ME_PATH = '/auth/me';

export const fetchMeta = () => api<Meta>('/meta');
export const fetchMe = () => api<Me>(ME_PATH);
/** Public, never 401: whether this browser is signed in (and as whom), so the first screen asks without an error. */
export const fetchSession = () => api<{ signedIn: false; step?: LoginStep } | { signedIn: true; me: Me }>('/auth/session');
/** Answers exactly what GET /auth/me does (scope with an operator's networkSize, permissions), or the second step. */
export const login = (email: string, password: string) => api<LoginAnswer>(LOGIN_PATH, { body: { email, password }, headers: { [TWO_STEP_HEADER]: '1' } });
/** The second step: an authenticator code or a recovery code completes the pending sign-in (sets the session cookie). */
export const verifyLogin = (code: string) => api<Me>(`${LOGIN_PATH}/verify`, { body: { code } });
export const logout = () => api('/auth/logout', { method: 'POST', body: {} });
